<script setup lang="ts">
import type { NuxtLinkProps } from "#app";
import type { RouteLocationNormalized } from "#vue-router";
import { NuxtLink } from "#components";
import isEqual from "lodash-es/isEqual";

interface NuxtLinkSlotProps {
  href: string;
  route: RouteLocationNormalized;
  navigate: (e?: MouseEvent) => Promise<void>;
  isActive: boolean;
  isExactActive: boolean;
  target?: string;
  rel?: string;
  isExternal?: boolean;
}

const currentRoute = useRoute();
const props = defineProps<
  NuxtLinkProps & {
    as?: string | Component;
    type?: HTMLButtonElement["type"];
    disabled?: boolean;
    exact?: boolean;
    exactQuery?: boolean;
    exactHash?: boolean;
    inactiveClass?: string;
  }
>();

function resolveLinkClass(
  route: RouteLocationNormalized,
  $route: RouteLocationNormalized,
  { isActive, isExactActive }: { isActive: boolean; isExactActive: boolean },
): string | undefined {
  if (props.exactQuery && !isEqual(route.query, $route.query))
    return props.inactiveClass;

  if (props.exactHash && route.hash !== $route.hash)
    return props.inactiveClass;

  if (props.exact && isExactActive)
    return props.activeClass;

  if (!props.exact && isActive)
    return props.activeClass;

  return props.inactiveClass;
}

function isExternalLink(href: string | undefined): boolean {
  if (!href)
    return false;
  try {
    const url = new URL(href, window.location.origin);
    return url.origin !== window.location.origin;
  }
  catch {
    return false;
  }
}

function getTargetAttribute(linkProps: NuxtLinkSlotProps): string | undefined {
  // Check if linkProps has target property, otherwise determine based on href
  if ("target" in linkProps && linkProps.target) {
    return linkProps.target;
  }

  // For external links, default to _blank
  return isExternalLink(linkProps.href) ? "_blank" : undefined;
}

function getRelAttribute(linkProps: NuxtLinkSlotProps): string | undefined {
  // Check if linkProps has rel property, otherwise determine based on href
  if ("rel" in linkProps && linkProps.rel) {
    return linkProps.rel;
  }

  // For external links, default to noopener noreferrer for security
  return isExternalLink(linkProps.href) ? "noopener noreferrer" : undefined;
}
</script>

<template>
  <Component
    :is="props.as || 'button'"
    v-if="!props.to && !props.href"
    v-bind="$attrs"
    :type="props.type"
    :disabled="props.disabled ? 'true' : undefined"
    :class="props.inactiveClass"
  >
    <slot />
  </Component>
  <NuxtLink
    v-slot="linkProps"
    v-bind="props"
    custom
  >
    <a
      v-bind="$attrs"
      :href="!props.disabled ? (linkProps as NuxtLinkSlotProps).href : undefined"
      :disabled="props.disabled ? 'true' : undefined"
      :aria-disabled="props.disabled ? 'true' : undefined"
      :role="props.disabled ? 'link' : undefined"
      :rel="getRelAttribute(linkProps as NuxtLinkSlotProps)"
      :target="getTargetAttribute(linkProps as NuxtLinkSlotProps)"
      :class="resolveLinkClass((linkProps as NuxtLinkSlotProps).route, currentRoute, { isActive: (linkProps as NuxtLinkSlotProps).isActive, isExactActive: (linkProps as NuxtLinkSlotProps).isExactActive })"
      @click="(e) => !isExternalLink((linkProps as NuxtLinkSlotProps).href) && !props.disabled && (linkProps as NuxtLinkSlotProps).navigate(e)"
    >
      <slot v-bind="{ isActive: props.exact ? (linkProps as NuxtLinkSlotProps).isExactActive : (linkProps as NuxtLinkSlotProps).isActive }" />
    </a>
  </NuxtLink>
</template>
